import type { AttributeAsset, AttributeEntityType, AttributeValueType } from './contracts';
import { ExtendableError } from '../../../utils/errors';

export const ATTRIBUTE_UPLOAD_ACTIONS = {
    replaceImage: 'replaceAttributeImage',
    appendImages: 'appendAttributeImages',
} as const;

type AttributeUploadAction =
    (typeof ATTRIBUTE_UPLOAD_ACTIONS)[keyof typeof ATTRIBUTE_UPLOAD_ACTIONS];

interface UploadAttributeFilesArgs {
    action: AttributeUploadAction;
    entityType: AttributeEntityType;
    entityId: string;
    attributeKey: string;
    valueType: AttributeValueType;
    files: File[];
    caption?: string;
    csrfToken: string | null | undefined;
}

interface AttributeUploadResult {
    valueType: AttributeValueType;
    image?: AttributeAsset | null;
    imageList?: AttributeAsset[];
}

export class AttributeUploadError extends ExtendableError {
    code: string | null;

    constructor(message: string, code: string | null = null) {
        super(message);
        this.code = code;
    }
}

function buildUploadFormData({
    action,
    entityType,
    entityId,
    attributeKey,
    valueType,
    files,
    caption,
}: UploadAttributeFilesArgs): FormData {
    const formData = new FormData();

    formData.set('action', action);
    formData.set('entityType', entityType);
    formData.set('entityId', entityId);
    formData.set('attributeKey', attributeKey);
    formData.set('valueType', valueType);
    files.forEach((file) => formData.append('files', file));
    if (caption !== undefined) {
        formData.set('caption', caption);
    }

    return formData;
}

export async function uploadAttributeFiles(
    args: UploadAttributeFilesArgs
): Promise<AttributeUploadResult> {
    const headers: HeadersInit = {};

    if (args.csrfToken) {
        headers['X-CSRFToken'] = args.csrfToken;
    }

    const response = await fetch('/attributes/upload/', {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: buildUploadFormData(args),
    });
    const payload = (await response.json()) as {
        ok?: boolean;
        attribute?: AttributeUploadResult;
        error?: { code?: string | null; message?: string | null };
    };

    if (!response.ok || !payload.ok) {
        throw new AttributeUploadError(
            payload.error?.message ?? gettext('ATTRIBUTE_UPLOAD_ERROR'),
            payload.error?.code ?? null
        );
    }

    if (!payload.attribute) {
        throw new AttributeUploadError(gettext('ATTRIBUTE_UPLOAD_ERROR'));
    }

    return payload.attribute;
}
